import { useState } from 'react';
import type { Reaction } from '../domain/types';
import { useApp } from '../app/AppState';
import { Icon } from './Icon';

/** The family's six responses — the same six wherever anyone can respond. */
export const REACTION_EMOJI = ['❤️', '😂', '😍', '👏', '😢', '🎉'] as const;

/**
 * The family's six responses, folded away — the one emoji control in the app.
 *
 * There used to be two: a flat row of six on a picture, and this. Jay:
 * *"that emoji system should be universal — anywhere else there's emojis, use
 * the same colour kind of system and layout."* He is right, and not only for
 * tidiness: six empty circles are a lot of furniture for something nobody has
 * used yet, and they read differently on a picture than on a story, which makes
 * the same gesture look like two features.
 *
 * So it closes down to one small button at the foot of the page: what the family has
 * already left, or — when nobody has yet — just a face and a plus, which is
 * enough to say "press this to add one". Tap it and the six open; pick one and
 * it closes again with yours counted among the rest. The words stay in the
 * label, for anyone hovering or listening.
 */
export function ReactionBar({
  reactions, onReact, label, addLabel,
}: {
  reactions: Reaction[] | undefined;
  onReact: (emoji: string | null) => void;
  /** Names the group for a screen reader. */
  label: string;
  /** What the closed control says when nobody has reacted yet. */
  addLabel: string;
}) {
  const { actor } = useApp();
  const [open, setOpen] = useState(false);
  const mine = actor ? reactions?.find((r) => r.accountId === actor.session.accountId)?.emoji ?? null : null;
  const counts = new Map<string, number>();
  for (const r of reactions ?? []) counts.set(r.emoji, (counts.get(r.emoji) ?? 0) + 1);
  const left = [...counts.entries()].sort((a, b) => b[1] - a[1]);

  /*
   * One pill, in both states, which is the whole point: opening it *widens* it
   * rather than replacing it with a different row. The six choices are always
   * in the DOM, clipped to no width until they are wanted; the tallies fold
   * away as they arrive, so the pill grows sideways in its own place and the
   * page below it never moves.
   */
  return (
    <div className={`reaction-bar ${open ? 'is-open' : ''}`}>
      <button
        type="button"
        className={`reaction-summary ${left.length === 0 ? 'is-empty' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={open ? label : left.length > 0 ? label : addLabel}
        title={open ? label : left.length > 0 ? label : addLabel}
      >
        <span className="reaction-tallies">
          {left.map(([e, n]) => (
            <span key={e} className={`reaction-tally ${mine === e ? 'is-mine' : ''}`}>
              <span className="reaction-emoji">{e}</span>
              <span className="reaction-count">{n}</span>
            </span>
          ))}
          {left.length === 0 && <span className="reaction-emoji" aria-hidden="true">🙂</span>}
        </span>
        <span className="reaction-add" aria-hidden="true"><Icon name={open ? 'close' : 'plus'} /></span>
      </button>
      <div className="reaction-choices" role="group" aria-label={label} aria-hidden={!open}>
        {REACTION_EMOJI.map((e) => (
          <button
            key={e}
            type="button"
            className={`reaction ${mine === e ? 'is-mine' : ''}`}
            tabIndex={open ? 0 : -1}
            onClick={() => {
              onReact(mine === e ? null : e);
              setOpen(false);
            }}
            aria-pressed={mine === e}
            aria-label={`${e} ${counts.get(e) ?? 0}`}
          >
            <span className="reaction-emoji">{e}</span>
            {counts.get(e) ? <span className="reaction-count">{counts.get(e)}</span> : null}
          </button>
        ))}
      </div>
    </div>
  );
}

/** "❤️ 3 · 👏 1" for a tile or a list row. */
export function reactionSummary(reactions: Reaction[] | undefined): string {
  const counts = new Map<string, number>();
  for (const r of reactions ?? []) counts.set(r.emoji, (counts.get(r.emoji) ?? 0) + 1);
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3).map(([e, n]) => `${e} ${n}`).join(' · ');
}
